import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

// Importaciones de entornos.
import { environment } from '../../environments/environment';

// Importaciones de servicios.
import { IpService } from './ip.service';

// Importaciones de interfaces.
import { ILog } from '../interfaces/log.interface';

/**
 * Servicio para gestionar los registros (logs) de la aplicación.
 *
 * Esta clase proporciona métodos para registrar información, advertencias y errores,
 * permitiendo un mejor seguimiento y depuración del sistema.
 */
@Injectable({
  providedIn: 'root',
})
export class LogService {
  /**
   * URL del endpoint para acceder a los registros.
   *
   * Esta propiedad almacena la URL a la que se enviarán las solicitudes HTTP
   * para obtener la lista de los registros disponibles en la aplicación.
   */
  private url: string = '/logs';

  /**
   * Constructor del servicio LogService.
   *
   * Inyecta las dependencias necesarias para la creación de registros de log.
   *
   * @param http - Servicio HttpClient de Angular utilizado para realizar peticiones HTTP a la API.
   * @param ipService - Servicio utilizado para obtener la dirección IP del cliente desde una API externa.
   */
  constructor(private http: HttpClient, private ipService: IpService) {}

  /**
   * Obtiene una lista de los registros con filtros opcionales.
   *
   * @param offset - Define el desplazamiento para la paginación. `null` si no se usa paginación.
   * @param limit - Establece el número máximo de registros a devolver. `null` para no limitar los resultados.
   *
   * @returns Una promesa que se resuelve con la lista de registros obtenidos.
   */
  public getAll(
    offset: number | null = null,
    limit: number | null = null
  ): Promise<any> {
    const params = Object.entries({ offset, limit }).reduce(
      (acc, [key, value]) =>
        value !== null ? acc.set(key, value.toString()) : acc,
      new HttpParams()
    );

    return firstValueFrom(
      this.http.get(environment.apiUrl + this.url, { params })
    );
  }

  /**
   * Crea un nuevo registro de log enviando los datos al servidor.
   *
   * Esta función obtiene la dirección IP del usuario a través del servicio `ipService`,
   * y luego realiza una petición HTTP POST al endpoint definido, enviando la IP,
   * el user agent del navegador y una descripción del log.
   *
   * @param log - Objeto que contiene la información del registro al ser creado.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la creación del registro.
   */
  async create(log: ILog): Promise<any> {
    const response = await this.ipService.get();

    return firstValueFrom(
      this.http.post(environment.apiUrl + this.url, {
        ipAddress: response.ip,
        userAgent: window.navigator.userAgent,
        description: log.description,
      })
    );
  }

  /**
   * Elimina todos los registros de la base de datos.
   *
   * Este método realiza una solicitud HTTP DELETE al endpoint correspondiente
   * para eliminar todos los registros almacenados en el sistema.
   *
   * @returns Una promesa que se resuelve con la respuesta del servidor después de la eliminación de los registros.
   */
  async deleteAll(): Promise<any> {
    return firstValueFrom(this.http.delete(environment.apiUrl + this.url));
  }
}
